import { storyAt } from './storyTime'
import { clampTornadoPresetWindow } from './tornadoPresets'
import type { PoseKeyframe, WindKeyframe } from './types'
import { metersFromMiles } from './format'

/**
 * Saffir-Simpson categories are 1-minute sustained wind at or above 74 mph.
 * A tropical storm is the same cyclone below that, so it has no category.
 * Tropical depression (under 39 mph) is left off: it is not this event.
 * Width is the wind field and is not implied by category. A Category 5 can
 * be small and a Category 1 can be huge. Every preset uses one field; Width overrides it.
 * The track is a 50-mile passage through the drop. Heading is the direction
 * the center travels; north is the default. Duration is how long that passage
 * takes, which is the forward speed. Wind is the circulation around that
 * heading: from the right on approach, through the eye, then from the left
 * on the back side. Due north, that is east, then south, then west.
 * Categories drop to a light wind in the eye. A tropical storm does not.
 */
export type HurricanePresetId =
  | 'tropical_storm'
  | 'cat1'
  | 'cat2'
  | 'cat3'
  | 'cat4'
  | 'cat5'

export type HurricanePresetDef = {
  id: HurricanePresetId
  label: string
  /** Sustained peak inside the category band. Shown with the name. */
  peakMph: number
  /** False for a tropical storm, which has no calm eye. */
  eye: boolean
  defaultSpanMs: number
}

export const HURRICANE_WIND_SPEED_MAX_MPH = 200

/** Direction the center travels. 0 is north. Wind directions rotate with this. */
export const HURRICANE_TRACK_HEADINGS: readonly { deg: number; label: string }[] = [
  { deg: 0, label: 'North' },
  { deg: 45, label: 'Northeast' },
  { deg: 90, label: 'East' },
  { deg: 135, label: 'Southeast' },
  { deg: 180, label: 'South' },
  { deg: 225, label: 'Southwest' },
  { deg: 270, label: 'West' },
  { deg: 315, label: 'Northwest' },
]

export function hurricaneTrackHeadingFromParams(params: Record<string, unknown>): number {
  const raw = params.trackHeadingDeg
  const deg = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
  if (!Number.isFinite(deg)) return 0
  const norm = ((Math.round(deg) % 360) + 360) % 360
  return HURRICANE_TRACK_HEADINGS.some((heading) => heading.deg === norm) ? norm : 0
}

export const HURRICANE_PRESET_SPAN_OPTIONS: { ms: number; label: string }[] = [
  { ms: 3 * 60 * 60_000, label: '3 hours' },
  { ms: 6 * 60 * 60_000, label: '6 hours' },
  { ms: 12 * 60 * 60_000, label: '12 hours' },
  { ms: 18 * 60 * 60_000, label: '18 hours' },
  { ms: 24 * 60 * 60_000, label: '24 hours' },
  { ms: 36 * 60 * 60_000, label: '36 hours' },
]

const SIX_HOURS = 6 * 60 * 60_000

export const HURRICANE_PRESETS: readonly HurricanePresetDef[] = [
  { id: 'tropical_storm', label: 'Tropical Storm', peakMph: 60, eye: false, defaultSpanMs: SIX_HOURS },
  { id: 'cat1', label: 'Category 1', peakMph: 85, eye: true, defaultSpanMs: SIX_HOURS },
  { id: 'cat2', label: 'Category 2', peakMph: 105, eye: true, defaultSpanMs: SIX_HOURS },
  { id: 'cat3', label: 'Category 3', peakMph: 120, eye: true, defaultSpanMs: SIX_HOURS },
  { id: 'cat4', label: 'Category 4', peakMph: 145, eye: true, defaultSpanMs: SIX_HOURS },
  { id: 'cat5', label: 'Category 5', peakMph: 165, eye: true, defaultSpanMs: SIX_HOURS },
]

export function hurricanePresetById(id: HurricanePresetId): HurricanePresetDef {
  return HURRICANE_PRESETS.find((p) => p.id === id) ?? HURRICANE_PRESETS[0]!
}

const HURRICANE_PRESET_IDS = new Set<string>(HURRICANE_PRESETS.map((p) => p.id))

export function hurricanePresetIdFromParams(params: Record<string, unknown>): HurricanePresetId | '' {
  const id = params.hurricanePresetId
  return typeof id === 'string' && HURRICANE_PRESET_IDS.has(id) ? (id as HurricanePresetId) : ''
}

export function hurricanePresetSpanFromParams(params: Record<string, unknown>): number | null {
  const raw = params.presetSpanMs
  const ms = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
  if (!Number.isFinite(ms)) return null
  return HURRICANE_PRESET_SPAN_OPTIONS.some((opt) => opt.ms === ms) ? ms : null
}

export type HurricanePresetBuild = {
  keyframes: PoseKeyframe[]
  windKeyframes: WindKeyframe[]
  params: {
    fromDeg: number
    speedMph: number
    plumeDistanceM: number
    plumeSpreadDeg: number
    showPlume: boolean
    radiusM: number
  }
}

function clampDeg(deg: number): number {
  return ((Math.round(deg) % 360) + 360) % 360
}

function atSpan(onsetMs: number, spanMs: number, u: number): number {
  return onsetMs + Math.round(Math.max(0, Math.min(1, u)) * Math.max(1, spanMs))
}

function offsetLngLat(
  origin: { lng: number; lat: number },
  eastM: number,
  northM: number,
): { lng: number; lat: number } {
  const dLat = northM / 111_320
  const dLng = eastM / (111_320 * Math.cos((origin.lat * Math.PI) / 180))
  return { lng: origin.lng + dLng, lat: origin.lat + dLat }
}

/** Positive distance is ahead along the heading. 0° heads north. */
function offsetAlongTrack(
  origin: { lng: number; lat: number },
  headingDeg: number,
  alongM: number,
): { lng: number; lat: number } {
  const rad = (headingDeg * Math.PI) / 180
  return offsetLngLat(origin, alongM * Math.sin(rad), alongM * Math.cos(rad))
}

/** One passage through the drop. The drop is the eye, halfway along a 50-mile track. */
const PASSAGE_ALONG_MI = [ -25, -8, 0, 8, 25 ] as const
const PASSAGE_U = [0, 0.35, 0.5, 0.65, 1] as const
/**
 * Circulation for a northbound center. Added to the track heading:
 * ahead of the center the wind is from the right, the eye is light,
 * and the back side is from the left.
 */
const PASSAGE_FROM_DEG = [90, 90, 180, 270, 270] as const

function speedAt(u: number, keys: readonly (readonly [number, number])[]): number {
  const uClamped = Math.max(0, Math.min(1, u))
  let i = 0
  while (i < keys.length - 1 && uClamped > keys[i + 1]![0]) i++
  const a = keys[i]!
  const b = keys[Math.min(keys.length - 1, i + 1)]!
  if (b[0] <= a[0]) return b[1]
  const t = (uClamped - a[0]) / (b[0] - a[0])
  return a[1] + (b[1] - a[1]) * Math.max(0, Math.min(1, t))
}

export function buildHurricanePreset(
  id: HurricanePresetId,
  opts: {
    origin: { lng: number; lat: number }
    onsetMs: number
    spanMs: number
    originIso: string
    /** Direction the center travels. 0 is north. */
    headingDeg?: number
  },
): HurricanePresetBuild {
  const preset = hurricanePresetById(id)
  const onset = Math.max(0, Math.round(opts.onsetMs))
  const span = Math.max(5 * 60_000, Math.round(opts.spanMs))
  const heading = clampDeg(opts.headingDeg ?? 0)
  const radiusM = metersFromMiles(12.5)
  const peak = preset.peakMph
  const outer = Math.max(25, Math.round(peak * 0.45))
  const eye = preset.eye ? 12 : Math.max(35, Math.round(peak * 0.7))
  const leave = Math.max(20, Math.round(peak * 0.35))
  const speeds: readonly (readonly [number, number])[] = [
    [0, outer],
    [0.35, peak],
    [0.5, eye],
    [0.65, peak],
    [1, leave],
  ]
  const points = PASSAGE_ALONG_MI.map((alongMi) =>
    alongMi === 0
      ? { lng: opts.origin.lng, lat: opts.origin.lat }
      : offsetAlongTrack(opts.origin, heading, alongMi * 1609.344),
  )
  const keyframes: PoseKeyframe[] = points.map((p, i) => ({
    at: storyAt(opts.originIso, atSpan(onset, span, PASSAGE_U[i]!)),
    lng: p.lng,
    lat: p.lat,
  }))
  const windKeyframes: WindKeyframe[] = points.map((_, i) => {
    const u = PASSAGE_U[i]!
    return {
      at: storyAt(opts.originIso, atSpan(onset, span, u)),
      fromDeg: clampDeg(PASSAGE_FROM_DEG[i]! + heading),
      speedMph: Math.max(0, Math.round(speedAt(u, speeds))),
      plumeDistanceM: Math.max(50, Math.round(radiusM)),
      plumeSpreadDeg: 28,
    }
  })
  const peakFrame = windKeyframes.reduce((best, frame) => (frame.speedMph > best.speedMph ? frame : best))
  return {
    keyframes,
    windKeyframes,
    params: {
      fromDeg: peakFrame.fromDeg,
      speedMph: peakFrame.speedMph,
      plumeDistanceM: radiusM,
      plumeSpreadDeg: 28,
      showPlume: true,
      radiusM,
    },
  }
}

export function buildClampedHurricanePreset(
  id: HurricanePresetId,
  opts: {
    origin: { lng: number; lat: number }
    onsetMs: number
    spanMs: number
    clockEndMs: number
    originIso: string
    headingDeg?: number
  },
): HurricanePresetBuild & { onsetMs: number; spanMs: number } {
  const clamped = clampTornadoPresetWindow(opts.onsetMs, opts.spanMs, opts.clockEndMs)
  const built = buildHurricanePreset(id, {
    origin: opts.origin,
    onsetMs: clamped.onsetMs,
    spanMs: clamped.spanMs,
    originIso: opts.originIso,
    headingDeg: opts.headingDeg,
  })
  return { ...built, onsetMs: clamped.onsetMs, spanMs: clamped.spanMs }
}
